
import { defineComponent } from "vue";

import getQrCodeService from "@/services/QrCodeService";

import Loader from "@/components/Loader.vue";
import Message from "@/components/Message.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import RGPDPopin from "@/components/RGPDPopin.vue";
import { mapState } from "vuex";
import { State } from "@/store";
import Consents from "@/models/Consents";

export default defineComponent({
    name: "Upload",
    components: {
        Loader,
        Message,
        ProgressBar,
        RGPDPopin
    },
    data() {
        return {
            messageOverlay: {},
            displayMessage: false,
            uploadProgression: 0,
            transcodeProgression: 0,
            transcodeProgressTimer: null as number | null,
            transcodeDuration: 5000,
            transcodeStep: 2,
            rgpdPopinDisplayed: false,
            startUploading: false,
            allowRetry: false
        };
    },
    computed: {
        isUploading() {
            return this.uploadProgression < 100;
        },
        canStartUpload() {
            if (this.qrcodeObject.fastUX) {
                return true;
            }

            return (
                this.termsAndConditionsConsent === true &&
                (!this.authenticated || typeof this.shareEmailConsent === "boolean")
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            videoUrl: (state: State) => state.qrcode.videoUrl,
            recordedVideoFile: (state: State) => state.record.videoFile,
            recordedVideoName: (state: State) => state.record.videoName,
            userData: (state: State) => ({
                firstName: state.record.userFirstName,
                lastName: state.record.userLastName,
                email: state.record.userEmail,
                phone: state.record.userPhone,
                videoAgreement: state.record.userVideoAgreement
            }),
            termsAndConditionsConsent: (state: State) => state.rgpd.termsAndConditionsConsent,
            shareEmailConsent: (state: State) => state.rgpd.shareEmailConsent,
            authenticated: (state: State) => state.auth.authenticated,
            askEmailConsent: (state: State) =>
                state.auth.authenticated &&
                state.qrcode.qrcodeObject.accountRequired !== "NO_ACCOUNT"
        })
    },
    watch: {
        qrcodeObject() {
            if (!this.qrcodeObject.experiences.length) {
                this.onServerError(true);
            } else {
                if (this.qrcodeObject.fastUX) {
                    this.$router.replace("/");
                } else {
                    this.$router.replace("success");
                }
            }
        },
        canStartUpload(newValue) {
            if (newValue) {
                this.startUpload();
            } else {
                this.displayRGPDPopin();
            }
        }
    },
    async mounted() {
        if (!this.authenticated && this.qrcodeObject.accountRequired === "REQUIRED") {
            this.$router.replace("SignIn");
        }

        if (!this.recordedVideoFile) {
            this.$router.replace("/");
            return;
        }

        const canFetchConsents =
            this.authenticated && this.qrcodeObject.accountRequired !== "NO_ACCOUNT";
        const getConsentsPromise = canFetchConsents
            ? this.$store.dispatch("rgpd/getConsents", this.qrcodeObject.qrcodeId)
            : Promise.resolve();

        try {
            await getConsentsPromise;
            if (!this.canStartUpload) {
                this.displayRGPDPopin();
            } else {
                this.startUpload();
            }
        } catch (e) {
            this.displayRGPDPopin();
        }
    },
    beforeUnmount() {
        if (this.transcodeProgressTimer) {
            clearInterval(this.transcodeProgressTimer);
        }
    },
    methods: {
        displayRGPDPopin() {
            this.rgpdPopinDisplayed = true;
        },

        async onRGPDClosed(consents: Consents) {
            await this.$store.dispatch("rgpd/updateConsents", {
                qrcodeId: this.qrcodeObject.qrcodeId,
                ...consents
            });
        },

        startUpload() {
            // to avoid any duplicate upload
            if (this.startUploading) {
                return;
            }

            this.startUploading = true;

            this.rgpdPopinDisplayed = false;
            getQrCodeService()
                .saveExperience(
                    this.recordedVideoFile,
                    this.recordedVideoName,
                    this.qrcodeObject,
                    this.userData,
                    this.onUploadProgress
                )
                .then((res) => {
                    if(!res){
                        this.allowRetry = true;
                        this.$router.replace("/");
                        this.onServerError(false, 'e');
                        return;
                    }
                        this.$store.dispatch("qrcode/load", this.qrcodeObject.qrcodeId);
                        this.allowRetry = false;
                })
                .catch(e => {
                    this.allowRetry = true;
                    console.error("error",e);
                    this.onServerError(false, e);
                });
        },
        onUploadProgress(progression: number) {
            this.uploadProgression = progression;

            if (progression >= 100) {
                this.transcodeProgressTimer = setInterval(
                    this.updateTranscodeProgression,
                    (this.transcodeDuration * this.transcodeStep) / 100
                );
            }
        },
        showMessage(_isError, title, text) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text
            };
            this.displayMessage = true;
        },
        onServerError(isError = true, e = null) {
            this.showMessage(
                isError,
                this.$t("general.serverError", e),
                this.$t("upload.uploadError")
            );
        },
        onMessageClosed() {
            this.displayMessage = false;
            this.$router.replace("record");
        },
        updateTranscodeProgression() {
            this.transcodeProgression = Math.min(
                this.transcodeProgression + this.transcodeStep,
                100
            );

            if (this.transcodeProgression === 100) {
                clearInterval(this.transcodeProgressTimer);
            }
        },
        resetTranscodeProgression() {
            clearInterval(this.transcodeProgressTimer);
            this.uploadProgression = 0;
            this.transcodeProgression = 0;
        },
        retryUpload() {
            this.resetTranscodeProgression();
            this.startUploading = false;
            this.startUpload();
        },
        retryHandler() {
            this.displayMessage = false;
            this.retryUpload();
        }
    }
});
